const ALIAS_PATTERN = /^[a-zA-Z0-9]+$/;

export const MAX_ASSIGNEES = 5;

/**
 * True when `input` is a well-formed employee alias (alphanumeric, at
 * least one character, ignoring surrounding whitespace). Reused by
 * `EmployeePicker`'s free-text fallback gate so a typed value that
 * downstream validation would reject never appears as a selectable
 * row in the dropdown. Kept alongside `validateAssigneeAlias` so both
 * sites — the picker fallback (pre-submit) and the modal validation
 * (submit) — share the same rule.
 */
export function isValidAlias(input: string): boolean {
  return ALIAS_PATTERN.test(input.trim());
}

/**
 * Discriminated error codes for assignee validation.
 * Components map these to translated strings via useMessages.
 *
 * Shared across features — audit manage-assignees and task reassign
 * modals validate aliases with the same rules.
 */
export const AssigneeValidationErrorCode = {
  REQUIRED: 'REQUIRED',
  INVALID_ALIAS: 'INVALID_ALIAS',
  DUPLICATE: 'DUPLICATE',
  MAX_REACHED: 'MAX_REACHED',
} as const;

export type AssigneeValidationErrorCode =
  (typeof AssigneeValidationErrorCode)[keyof typeof AssigneeValidationErrorCode];

export type AssigneeValidationResult =
  | { valid: true }
  | { valid: false; errorCode: AssigneeValidationErrorCode };

export function validateAssigneeAlias(
  alias: string,
  currentAssignees: string[],
): AssigneeValidationResult {
  const trimmed = alias.trim().toLowerCase();

  if (!trimmed) {
    return { valid: false, errorCode: AssigneeValidationErrorCode.REQUIRED };
  }

  if (!ALIAS_PATTERN.test(trimmed)) {
    return { valid: false, errorCode: AssigneeValidationErrorCode.INVALID_ALIAS };
  }

  if (currentAssignees.some((a) => a.toLowerCase() === trimmed)) {
    return { valid: false, errorCode: AssigneeValidationErrorCode.DUPLICATE };
  }

  if (currentAssignees.length >= MAX_ASSIGNEES) {
    return { valid: false, errorCode: AssigneeValidationErrorCode.MAX_REACHED };
  }

  return { valid: true };
}
